import { customElement } from 'lit/decorators.js'
import { html, LitElement, nothing, type CSSResultGroup } from 'lit'
import { hostStyle } from './delivery-match-method.style.js'
import type { DeliveryMethod } from './api/shipment-methods.js'

import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { consume } from '@lit/context'
import { DeliveryMatchActionsContext, DeliveryMatchCurrencyContext, DeliveryMatchSelectedMethodContext, type DeliveryMatchActionsContextType, type DeliveryMatchCurrencyContextType, type DeliveryMatchSelectedMethodContextType } from './provider.js'
import { capitalizeString, getCarrierImage, getRelativeDateInfo, priceFormat } from './utils.js'

@customElement('csc-delivery-match-method')
export class CSCDeliveryMatchMethod extends LitElement {
  method: DeliveryMethod
  showRadioButton?: boolean

  @consume({ context: DeliveryMatchActionsContext })
  private actions!: DeliveryMatchActionsContextType

  @consume({ context: DeliveryMatchSelectedMethodContext, subscribe: true })
  selectedMethod!: DeliveryMatchSelectedMethodContextType

  @consume({ context: DeliveryMatchCurrencyContext })
  currencyContext!: DeliveryMatchCurrencyContextType

  static override styles?: CSSResultGroup = [hostStyle]

  handleChangeMethod = () => {
    this.actions.setSelectedMethod(this.method.methodId)
  }

  renderDeliveryMoment = () => {
    // Logic
    /*
      When the diffInDays < 2 (today or tomorrow)
      -> Show (Today | Tomorrow) + (Weekday)
      Otherwise
      -> Show (Weekday + Day + Month)
    */

    const deliveryDate = this.method.deliveryDate.date

    if (!deliveryDate) {
      return html`<cse-text variant="heading-5" class="method-name">
        ${t('no_date_found', { ns: 'csc-delivery-match', default: 'No date found' })}
      </cse-text>`
    }

    const { date, relativeDate, diff, weekday } = getRelativeDateInfo(deliveryDate, new Date(), this.currencyContext.locale)

    if (diff < 2) {
      return html` <cse-text variant="heading-5" class="method-name">${capitalizeString(relativeDate)} (${capitalizeString(weekday)})</cse-text> `
    }

    return html`<cse-text variant="heading-5" class="method-name">${capitalizeString(weekday)} ${date}</cse-text>`
  }

  renderPrice = () => {
    const priceAsNumber = parseFloat(this.method.price)

    if (isNaN(priceAsNumber) || priceAsNumber === 0) {
      return html`<span class="method-price-wrapper"><cse-text class="method-price method-price-free">
        ${t('free_delivery', { ns: 'csc-delivery-match', default: 'Free delivery' })}
      </cse-text></span>`
    }

    const formattedPrice = priceFormat(this.currencyContext.locale, this.method.currency, this.method.price)
    return html`<span class="method-price-wrapper"><cse-text class="method-price">${formattedPrice}</cse-text></span>`
  }

  renderDescription = () => {
    const { timeFrom, timeTo } = this.method

    if (!(timeFrom || timeTo)) return nothing

    return html`<cse-text class="carrier-description">${timeFrom} - ${timeTo}</cse-text>`
  }

  renderCarrier = () => {
    // TODO: Find a way to show the carrier images.
    // For now, testing assets it is...
    return html`<cse-image
      class="carrier-image"
      source="${getCarrierImage(this.method.carrier.code)}"
      fallback-source="${getCarrierImage('fallback')}"
      alt="${this.method.carrier.name}"
      fit="contain"
      height="${40}"
    />`
  }

  constructor({ method, showRadioButton }: { method: DeliveryMethod, showRadioButton?: boolean }) {
    super()
    this.method = method
    this.showRadioButton = showRadioButton ?? false
  }

  private renderRadioButton = () => {
    if (!this.showRadioButton) return nothing
    return html`
      <cse-radio-button
        id="delivery-match-method-${this.method.methodId}"
        value=${this.method.methodId}
        ?checked=${this.selectedMethod === this.method.methodId}
      ></cse-radio-button>
    `
  }

  override render() {
    const isChecked = this.selectedMethod === this.method.methodId

    return html`<cse-selectable
      size="small"
      state="${isChecked ? 'selected' : 'selectable'}"
      @click="${this.handleChangeMethod}"
    >
      <div class="option">
        ${this.renderRadioButton()}
        <div class="option-inner">
          <cse-group direction="column">
            ${this.renderDeliveryMoment()}
            ${this.renderDescription()}
          </cse-group>
          <div class="option-content">${this.renderCarrier()} ${this.renderPrice()}</div>
        </div>
      </div>
    </cse-selectable>`
  }
}
